import { DateTime } from 'luxon';
import {
  dashIfFalsy,
  formatAddressMultiline,
  formatComplaintNotes,
  formatDateFromApiLocalToISOShort,
  formatName,
  formatNotes,
  formatAppointmentAbortDetails,
  formatVulnerabilities,
  getMostRecentComplaint,
  sortByDate,
  formatPsrDetails
} from 'components/JobDetailsSheet/jobsHelper';
import {
  AppointmentType,
  BookingChannel,
  CustomerAppointmentDetails,
  CustomerAppointmentType,
  EnergyExpertsDetails,
  energyExpertsHumanFriendlyTextMap,
  EVAppointmentDetails,
  EVFormChannel,
  EvIsolatorAppointmentDetails,
  MaintenanceDetails
} from 'models/Appointment';
import {
  IconType,
  JobDetailsResponse,
  JobSheetData,
  JobSheetDataRowsOptions,
  JobSheetSectionLabels
} from 'models/JobDetails';
import { redirectToEngineer, redirectToJob } from 'utils';
import { API_LOCAL_DATE_TIME_FORMAT } from 'constants/dates';
import { DATE_FOUR_G_AVAILABILITY_DATA_IS_ACCURATE_FROM } from 'constants/job';

export function getCustomerRows(
  job: JobDetailsResponse,
  newJobSheet: boolean
): JobSheetDataRowsOptions[] {
  const isEvJob = job?.appointmentType?.type === CustomerAppointmentType.EV;
  return [
    {
      type: 'text',
      label: 'Name',
      value: formatName(job.contactDetails)
    },
    ...(isEvJob
      ? []
      : [
          {
            type: 'text',
            label: newJobSheet ? 'Account number' : 'Account Number',
            value: dashIfFalsy(job.accountNumber)
          }
        ]),
    {
      type: 'text',
      label: 'Phone number',
      value: dashIfFalsy(job.contactDetails?.primaryPhoneNumber)
    },
    {
      type: 'text',
      label: 'Secondary number',
      value: dashIfFalsy(job.contactDetails?.secondaryPhoneNumber)
    },
    {
      type: job?.address ? 'textarea' : 'text',
      label: 'Address',
      value: formatAddressMultiline(job.address)
    }
  ];
}

export function getVulnerabilitiesRow(
  job: JobDetailsResponse,
  newJobSheet: boolean,
  type: string
): JobSheetDataRowsOptions {
  return {
    type: job?.customerDetails?.vulnerability ? type : 'text',
    label: newJobSheet ? 'Additional Vulnerabilities' : 'Vulnerabilities',
    value: formatVulnerabilities(job?.customerDetails?.vulnerability),
    icon: IconType.VULNERABLE
  };
}

export function getPsrVulnerabilitiesRow(
  job: JobDetailsResponse,
  newJobSheet: boolean,
  type: string
): JobSheetDataRowsOptions {
  return {
    type: job?.psrDetails?.vulnerabilities ? type : 'text',
    label: newJobSheet ? 'Vulnerabilities' : 'PSR Vulnerabilities',
    value: formatPsrDetails(job?.psrDetails?.vulnerabilities),
    icon: IconType.VULNERABLE
  };
}

function getCustomerSectionData(job: JobDetailsResponse): JobSheetData {
  return {
    label: JobSheetSectionLabels.Customer,
    rows: [
      ...getCustomerRows(job, false),
      getVulnerabilitiesRow(job, false, 'textarea'),
      getPsrVulnerabilitiesRow(job, false, 'textarea')
    ]
  };
}

export function getBookingRows(
  job: JobDetailsResponse,
  newJobSheet: boolean
): JobSheetDataRowsOptions[] {
  const { jumptechProjectId } =
    (job?.appointmentType?.details as EVAppointmentDetails) || {};
  const jumptechURL = jumptechProjectId
    ? `https://ovo-drive.jumptech.co.uk/project/${jumptechProjectId}`
    : null;

  return [
    {
      type: 'text',
      label: 'Status',
      value: dashIfFalsy(job.status)
    },
    {
      type: 'text',
      label: 'Reference number',
      value: dashIfFalsy(job.bookingReference)
    },
    {
      type: 'text',
      label: 'Engineer',
      onClick: () =>
        job.assignedEngineer && redirectToEngineer(job.assignedEngineer.id),
      value: dashIfFalsy(
        job.assignedEngineer &&
          `${job.assignedEngineer.firstName} ${job.assignedEngineer.lastName}`
      )
    },
    {
      type: 'text',
      label: 'Date',
      value: dashIfFalsy(job?.slot.date)
    },
    {
      type: 'text',
      label: newJobSheet ? 'Time slot' : 'Timeslot',
      value: dashIfFalsy(`${job.slot.startTime}-${job.slot.endTime}`)
    },
    {
      type: 'text',
      label: 'Job type',
      value: dashIfFalsy(job?.jobTypeData.name)
    },
    ...(() => {
      switch (job.appointmentType?.type) {
        case CustomerAppointmentType.EV:
          return [
            {
              type: 'text',
              label: 'Zoho account number',
              value: dashIfFalsy(
                (job?.appointmentType?.details as EVAppointmentDetails)
                  .zohoAccountId
              )
            },
            {
              type: 'text',
              label: 'Channel',
              value: Object.fromEntries(Object.entries(EVFormChannel))[
                (job?.appointmentType?.details as EVAppointmentDetails).channel
              ] as string
            },
            {
              type: jumptechURL ? 'link' : 'text',
              label: 'Jumptech URL',
              value: dashIfFalsy(jumptechURL)
            }
          ];
        case CustomerAppointmentType.EV_ISOLATOR:
          return [
            {
              type: 'text',
              label: 'Channel',
              value: Object.fromEntries(Object.entries(EVFormChannel))[
                (job?.appointmentType?.details as EvIsolatorAppointmentDetails)
                  .channel
              ] as string
            }
          ];
        default:
          return [
            {
              type: 'text',
              label: 'Meter type',
              value: dashIfFalsy(job?.appointmentType?.type)
            },
            {
              type: 'text',
              label: 'Payment type',
              value: dashIfFalsy(job?.appointmentType?.paymentType)
            },
            {
              type: 'text',
              label: 'Brand',
              value: dashIfFalsy(job?.brand)
            }
          ];
      }
    })()
  ];
}

export function getBookingNotesRow(
  job: JobDetailsResponse,
  newJobSheet: boolean,
  type: string
): JobSheetDataRowsOptions {
  return {
    type: job.notes.length > 0 ? type : 'text',
    label: newJobSheet ? 'Booking Notes' : 'Booking notes',
    value: formatNotes(job?.notes),
    icon: newJobSheet ? IconType.ERROR : IconType.INFO
  };
}

function getBookingSectionData(job: JobDetailsResponse): JobSheetData {
  return {
    label: JobSheetSectionLabels.Booking,
    rows: [
      ...getBookingRows(job, false),
      getBookingNotesRow(job, false, 'textarea')
    ]
  };
}

function getCommonTechnicalData(
  details: CustomerAppointmentDetails | MaintenanceDetails
): JobSheetDataRowsOptions[] {
  return [
    {
      type: 'text',
      label: 'Primary MPAN',
      value: dashIfFalsy(details.mpan)
    },
    {
      type: 'text',
      label: 'Secondary MPAN',
      value: dashIfFalsy(
        'secondaryMpan' in details ? details.secondaryMpan : undefined
      )
    },
    {
      type: 'text',
      label: 'MPRN',
      value: dashIfFalsy((details as CustomerAppointmentDetails).mprn)
    }
  ];
}

function getElecTariff(
  details: MaintenanceDetails | CustomerAppointmentDetails
): JobSheetDataRowsOptions[] {
  return [
    {
      type: 'text',
      label: 'Elec tariff',
      value: dashIfFalsy(details?.elecTariff)
    }
  ];
}

function getSmartTechnicalSectionData(
  appointmentType: AppointmentType,
  newJobSheet: boolean,
  createdAt?: string,
  bookingChannel?: string | null
): JobSheetData {
  const details = appointmentType.details as CustomerAppointmentDetails;
  const displayMultiMeterFields = details.secondaryMpan;

  const fourGAvailabilityDataIsPresent =
    bookingChannel !== BookingChannel.AEGIS &&
    (createdAt
      ? DateTime.fromFormat(createdAt, API_LOCAL_DATE_TIME_FORMAT) >
        DateTime.fromFormat(
          DATE_FOUR_G_AVAILABILITY_DATA_IS_ACCURATE_FROM,
          API_LOCAL_DATE_TIME_FORMAT
        )
      : false);
  const fourGAvilabailityData = details.meterTechnicalDetails?.xvali4g
    ? 'True'
    : 'False';

  return {
    label: JobSheetSectionLabels.Technical,
    rows: [
      ...getCommonTechnicalData(details),
      ...[
        {
          type: 'text',
          label: `Elec MSN${displayMultiMeterFields ? ' (Exchange)' : ''}`,
          value: dashIfFalsy(details.meterTechnicalDetails?.oldElecMsn)
        },
        ...(displayMultiMeterFields
          ? [
              {
                type: 'text',
                label: 'Elec MSN (Remove)',
                value: dashIfFalsy(
                  details.meterTechnicalDetails?.metersToBeRemoved.find(
                    meter => {
                      if ('mpan' in meter) {
                        return meter.mpan === details.secondaryMpan;
                      }

                      return false;
                    }
                  )?.serialNumber
                )
              }
            ]
          : []),
        {
          type: 'text',
          label: 'Gas MSN',
          value: dashIfFalsy(details.meterTechnicalDetails?.oldGasMsn)
        },
        {
          type: 'text',
          label: 'Expected new meter mode',
          value: dashIfFalsy(appointmentType.smartMakeModel)
        },
        {
          type: 'text',
          label: 'Semi-concealed',
          value: dashIfFalsy(details.hasConcealedMeter ? 'Yes' : 'No')
        },
        {
          type: 'text',
          label: 'WAN aux requirement',
          value: dashIfFalsy(
            details.meterTechnicalDetails?.xvali?.auxiliaryEquipmentRequired
          )
        },
        {
          type: 'text',
          label: 'WAN technology',
          value: dashIfFalsy(details.meterTechnicalDetails?.xvali?.commsHubType)
        },
        {
          type: 'text',
          label: newJobSheet ? 'CSP Region' : 'CSP region',
          value: dashIfFalsy(details.meterTechnicalDetails?.xvali?.cspRegion)
        },
        {
          type: 'text',
          label: '4G available',
          value: fourGAvailabilityDataIsPresent ? fourGAvilabailityData : 'N/A'
        },
        ...getElecTariff(details)
      ]
    ]
  };
}

function getMaintenanceTechnicalSectionData(
  details: MaintenanceDetails
): JobSheetData {
  return {
    label: JobSheetSectionLabels.Technical,
    rows: [
      {
        type: 'text',
        label: 'Emergency booking',
        value: dashIfFalsy(details.isEmergency ? 'Yes' : 'No')
      },
      ...getCommonTechnicalData(details),
      {
        type: 'text',
        label: 'Job description',
        value: dashIfFalsy(details.jobDescription)
      },
      {
        type: 'text',
        label: 'Old elec MSN',
        value: dashIfFalsy(details.oldElecMsn)
      },
      {
        type: 'text',
        label: 'Old SSC',
        value: dashIfFalsy(details.oldSsc)
      },
      {
        type: 'text',
        label: 'Old CT Ratio',
        value: dashIfFalsy(details.oldCtRatio)
      },
      {
        type: 'text',
        label: 'Old elec meter location',
        value: dashIfFalsy(details.oldElecMeterLocation)
      },
      {
        type: 'text',
        label: 'Old meter type',
        value: dashIfFalsy(details.oldMeterType)
      },
      {
        type: 'text',
        label: 'Old gas MSN',
        value: dashIfFalsy(details.oldGasMsn)
      },
      {
        type: 'text',
        label: 'Old gas meter location',
        value: dashIfFalsy(details.oldGasMeterLocation)
      },
      {
        type: 'text',
        label: 'New SSC',
        value: dashIfFalsy(details.newSsc)
      },
      {
        type: 'text',
        label: 'Storage heaters / underfloor heating',
        value: dashIfFalsy(details.storageUnderfloorHeating)
      },
      {
        type: 'text',
        label: 'New meter type',
        value: dashIfFalsy(details.newMeterType)
      },
      {
        type: 'text',
        label: 'New payment type',
        value: dashIfFalsy(details.newPaymentType)
      },
      {
        type: 'text',
        label: 'Meter mode',
        value: dashIfFalsy(details.meterMode)
      },
      {
        type: 'text',
        label: 'Energisation status',
        value: dashIfFalsy(details.energisationStatus)
      },
      {
        type: 'text',
        label: 'Meter positioning',
        value: dashIfFalsy(details.meterPositioning)
      },
      {
        type: 'text',
        label: 'Pressure tier',
        value: dashIfFalsy(details.pressureTier)
      },
      ...getElecTariff(details)
    ]
  };
}

function getEnergyExpertsTechnicalSectionData(details: EnergyExpertsDetails) {
  return {
    label: JobSheetSectionLabels.Technical,
    rows: [
      {
        type: 'textarea',
        label: 'Motivations',
        value: dashIfFalsy(
          details.motivations.map(
            motivation => energyExpertsHumanFriendlyTextMap[motivation]
          )
        )
      },
      {
        type: 'text',
        label: 'Additional resident information',
        value: dashIfFalsy(details.additionalResidentInfo)
      },
      {
        type: 'text',
        label: 'Other reason',
        value: dashIfFalsy(details.otherReasonDetail)
      }
    ]
  };
}

function getEvIsolatorTechnicalSectionData(
  details: EvIsolatorAppointmentDetails
) {
  return {
    label: JobSheetSectionLabels.Technical,
    rows: [
      {
        type: 'text',
        label: 'MPAN',
        value: dashIfFalsy(details.mpan)
      },
      {
        type: 'text',
        label: 'Elec MSN',
        value: dashIfFalsy(details.elecMsn)
      }
    ]
  };
}

function getEvTechnicalSectionData(details: EVAppointmentDetails) {
  return {
    label: JobSheetSectionLabels.Technical,
    rows: [
      {
        type: 'text',
        label: 'MPAN',
        value: dashIfFalsy(details.mpan)
      }
    ]
  };
}

export function getTechnicalSectionData(
  job: JobDetailsResponse,
  newJobSheet: boolean
): JobSheetData[] {
  const { appointmentType } = job;
  if (!appointmentType?.details) {
    return [];
  }

  switch (appointmentType.type) {
    case CustomerAppointmentType.MAINTENANCE:
      return [
        getMaintenanceTechnicalSectionData(
          appointmentType.details as MaintenanceDetails
        )
      ];
    case CustomerAppointmentType.ENERGY_EXPERTS:
      return [
        getEnergyExpertsTechnicalSectionData(
          appointmentType.details as EnergyExpertsDetails
        )
      ];
    case CustomerAppointmentType.EV_ISOLATOR:
      return [
        getEvIsolatorTechnicalSectionData(
          appointmentType.details as EvIsolatorAppointmentDetails
        )
      ];
    case CustomerAppointmentType.EV:
      return [
        getEvTechnicalSectionData(
          appointmentType.details as EVAppointmentDetails
        )
      ];
    default:
      return [
        getSmartTechnicalSectionData(
          appointmentType,
          newJobSheet,
          job.createdAt,
          job.bookingChannel
        )
      ];
  }
}

export function getComplaintSectionData(
  details: JobDetailsResponse
): JobSheetData[] {
  const complaint = getMostRecentComplaint(details.complaints?.slice());
  if (!complaint) {
    return [];
  }

  return [
    {
      label: JobSheetSectionLabels.Complaint,
      rows: [
        {
          type: 'text',
          label: 'Origin',
          value: dashIfFalsy(complaint?.origin)
        },
        {
          type: 'text',
          label: 'Severity',
          value: dashIfFalsy(
            complaint?.severity ? String(complaint?.severity) : null
          )
        },
        {
          type: 'text',
          label: 'Category',
          value: dashIfFalsy(complaint?.category)
        },
        {
          type: 'text',
          label: 'Date raised',
          value: dashIfFalsy(
            complaint
              ? formatDateFromApiLocalToISOShort(complaint.createdDate)
              : null
          )
        },
        {
          type: 'textarea',
          label: 'Complaint notes',
          value: dashIfFalsy(formatComplaintNotes(complaint)),
          icon: IconType.INFO
        }
      ]
    }
  ];
}

export function getPreviousAbortRows(
  job: JobDetailsResponse,
  newJobSheet: boolean
): JobSheetDataRowsOptions[] {
  const { previousAbortData } = job;
  if (previousAbortData === null) {
    return [];
  }
  return sortByDate([...previousAbortData.aborts])
    .filter(abort => abort.appointmentId !== job.id)
    .map((appointmentAbortDetails, index) => {
      return {
        type: 'textarea',
        label: `Abort ${index + 1}`,
        value: formatAppointmentAbortDetails(appointmentAbortDetails),
        onClick: () => redirectToJob(appointmentAbortDetails.appointmentId),
        ...(newJobSheet ? {} : { onClickRowValueIndex: 0 }),
        icon: IconType.INFO
      };
    });
}

function getPreviousAbortSectionData(job: JobDetailsResponse): JobSheetData[] {
  const previousAbortRows = getPreviousAbortRows(job, false);
  if (previousAbortRows.length === 0) {
    return [];
  }

  return [
    {
      label: JobSheetSectionLabels.PreviousAborts,
      rows: previousAbortRows
    }
  ];
}

export function getJobSheetData(job: JobDetailsResponse): JobSheetData[] {
  return [
    getCustomerSectionData(job),
    ...getComplaintSectionData(job),
    ...getPreviousAbortSectionData(job),
    getBookingSectionData(job),
    ...getTechnicalSectionData(job, false)
  ];
}
